<script lang="ts">
  import { cn } from '$lib/utils';
  import { cva, type VariantProps } from 'class-variance-authority';

  export let href: string;
  export let label: string;
  export let size: VariantProps<typeof labelStyles>['size'] = 'lg';
  export let image: { src: string; alt: string } | undefined = undefined;
  export let mobile_image: { src: string; alt: string } | undefined = undefined;
  export let open_new_tab: boolean | undefined = undefined;

  let className: string | undefined = undefined;
  export { className as class };

  const labelStyles = cva(
    ['w-full pointer-events-none text-primary/96 select-none text-4xl font-semibold'],
    {
      variants: {
        size: {
          micro: 'text-sm font-medium mt-1.5',
          sm: 'absolute px-2 left-1/2 top-1/2 z-10 text-center text-lg font-medium -translate-x-1/2 -translate-y-1/2',
          md: 'absolute px-2 left-1/2 top-1/2 z-10 text-center text-2xl -translate-x-1/2 -translate-y-1/2',
          lg: 'absolute px-2 left-1/2 top-1/2 z-10 text-center -translate-x-1/2 -translate-y-1/2'
        },
        image: {
          true: 'text-highlight'
        }
      },
      compoundVariants: [
        { image: true, size: 'micro', class: 'text-primary/96' },
        {
          image: false,
          size: 'micro',
          class:
            'absolute px-4 left-1/2 text-center mt-0 top-1/2 z-10 font-semibold -translate-x-1/2 -translate-y-1/2'
        }
      ]
    }
  );

  const imageContainer = cva(
    ['relative isolate h-full w-full overflow-hidden rounded-sm lg:rounded-md'],
    {
      variants: {
        size: {
          micro: 'aspect-[114/80]',
          sm: '',
          md: '',
          lg: ''
        }
      }
    }
  );
</script>

<a
  {href}
  target={open_new_tab ? '_blank' : undefined}
  class={cn(
    'relative block rounded-sm border-none shadow-none outline-none transition-shadow focus-visible:ring motion-reduce:transition-none lg:rounded-md',
    className
  )}
>
  <div class={cn(imageContainer({ size }))}>
    <div
      class={cn(
        'absolute inset-0',
        !image && 'bg-primary/4',
        image &&
          'bg-primary/18 transition-colors hover:bg-primary/14 motion-reduce:transition-none',
        image &&
          size === 'sm' &&
          'bg-primary/8 transition-colors hover:bg-primary/14 motion-reduce:transition-none'
      )}
    />
    {#if image}
      {@const { src, alt } = image}

      <img
        {src}
        {alt}
        draggable="false"
        class={cn(
          'h-full w-full select-none object-cover lg:block',
          !mobile_image ? 'block' : 'hidden'
        )}
      />
    {/if}

    {#if mobile_image}
      {@const { src, alt } = mobile_image}
      <img
        {alt}
        {src}
        draggable="false"
        class="h-full w-full select-none object-cover block lg:hidden"
      />
    {/if}
  </div>

  <span class={cn(labelStyles({ size, image: !!image }))}>
    {label}
  </span>
</a>
